import { Toaster, type ToasterProps } from "sonner";

export type ToastTheme = "light" | "dark";

export type Props = {
  /** Toaster position on the screen */
  position?: ToasterProps["position"];
  /** Default duration in milliseconds for toasts to remain visible */
  duration?: number;
  /** Visual theme for the base toast surface. Defaults to "light". */
  theme?: ToastTheme;
  /** Distance (px) from the chosen edge. Use a larger value on pages with a
   *  sticky action bar so the toast clears it. */
  offset?: ToasterProps["offset"];
};

/**
 * Global toast host. Mount once near the root of the page (e.g., in a layout)
 * so any component below can call `toast`, `toast.success(...)` or
 * `toast.error(...)` and have a styled toast appear.
 *
 * Variants:
 * - default — brand surface (`bg-brand-50` + `text-brand-800`)
 * - `toast.success(...)` — text + border in `text-success` / `border-success`
 * - `toast.error(...)` — text + border in `text-error` / `border-error`
 *
 * The `theme` prop only changes the base surface; success/error variants
 * keep their semantic colors on top of either theme.
 */
export function Toast({
  position = "bottom-center",
  duration = 3000,
  theme = "light",
  offset,
}: Props = {}) {
  // sonner's `[data-sonner-toast][data-styled="true"]` selector has higher
  // specificity than a single Tailwind utility class, so every utility that
  // needs to win against sonner's defaults is marked `!important`.
  const isDark = theme === "dark";
  const baseClass = isDark
    ? "!bg-brand-800 !text-brand-50 !border !border-brand-50 !shadow-lg !rounded-2xl"
    : "!bg-brand-50 !text-brand-800 !border !border-brand-800 !shadow-lg !rounded-2xl";

  // Semantic variants override only the text + border colors so the brand
  // surface is preserved. Variant colors are theme-aware: dark mode uses a
  // softer success (brand-100) and a lighter error (error-300) so they read
  // well against the brand-800 surface.
  const successClass = isDark
    ? "!text-brand-100 !border-brand-100"
    : "!text-success !border-success";
  const errorClass = isDark
    ? "!text-error-400 !border-error-400"
    : "!text-error-600 !border-error-600";

  return (
    <Toaster
      position={position}
      offset={offset}
      toastOptions={{
        duration,
        classNames: {
          toast: baseClass,
          // Match the `Type` `caption` variant: text-sm, font-normal.
          title: "!text-sm !font-normal",
          success: successClass,
          error: errorClass,
        },
      }}
    />
  );
}

Toast.displayName = "Toast";

export default Toast;
